import React from 'react';
import cn from 'classnames';
import { Avatar } from '@odin-labs/components';
import { getFormattedPhoneNumber, getInitialsForUser } from 'utils';
import { EditableEnhancedFormSubmissionWorker } from 'containers/jobsiteFormSubmission/types';
import {
  BarcodeReadIcon,
  CalendarXmarkIcon,
  ClockIcon,
  PhoneIcon,
  TimesIcon,
} from 'components/icons';
import { getPrettyFormattedDateTime } from 'utils/dates';

export const classes = {
  itemContainer: (isClickable: boolean): string =>
    cn(
      isClickable && 'odin-cursor-pointer',
      'odin-flex odin-flex-col odin-grow',
      'odin-border odin-border-gray-200 odin-shadow-sm odin-rounded-lg',
      'odin-p-6 odin-space-y-2',
      'sm:odin-flex-row sm:odin-justify-between sm:odin-space-x-4',
    ),
  workerInfoRow: cn(
    'odin-flex odin-flex-col',
    'odin-space-y-1.5',
    'sm:odin-flex-row sm:odin-space-x-6',
  ),
  workerNameContainer: cn('odin-flex odin-items-center'),
  workerName: cn(
    'odin-py-1',
    'odin-ml-5',
    'odin-text-base',
    'odin-font-medium',
    'odin-text-gray-900',
    'odin-truncate',
  ),
  workerInfoContainer: cn(
    'odin-grid',
    'odin-grid-cols-2',
    'odin-gap-y-1.5',
    'odin-ml-11',
    'sm:odin-flex',
    'sm:odin-space-x-4',
  ),
  workerInfoItem: cn(
    'worker-info-item-container',
    'odin-flex',
    'odin-items-center',
    'odin-text-1.5xs',
    'odin-truncate',
  ),
  workerInfoItemIcon: cn(
    'worker-info-item-icon',
    'odin-text-odin-primary',
    'odin-text-sm',
    'odin-font-normal',
    'odin-mr-1.5',
  ),
  participantTypeContainer: cn('odin-space-x-5'),
  removeIcon: cn(
    'worker-info-item-icon',
    'odin-text-gray-600',
    'odin-w-5 odin-h-5',
    'odin-cursor-pointer',
    'hover:odin-text-red-600',
  ),
  clockIcon: cn(
    'odin-text-xs',
    'odin-absolute odin-ml-12 odin-bottom-0',
    'odin-bg-odin-success odin-text-white odin-rounded-full odin-p-1',
  ),
};

export type FormSubmissionVisitorProps = {
  jfsWorker: EditableEnhancedFormSubmissionWorker;
  onRemoveIconClick?: () => void;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
};

export function FormSubmissionVisitor(
  props: FormSubmissionVisitorProps,
): React.ReactElement {
  const { jfsWorker, onRemoveIconClick, onClick } = props;

  const { jobsiteWorker } = jfsWorker ?? {};
  const {
    activeTempWorkerCard,
    contractorWorker: { worker },
    isOnSite,
  } = jobsiteWorker ?? {};
  const { user, profilePicture } = worker ?? {};
  const { firstName, lastName, phoneNumber } = user?.identity ?? {};
  const { downloadUrl: imageUrl } = profilePicture ?? {};
  const { expiredAt, tempBadge } = activeTempWorkerCard ?? {};

  return (
    <div className={classes.itemContainer(!!onClick)} onClick={onClick}>
      <div className={classes.workerInfoRow}>
        <div className={classes.workerNameContainer}>
          <div className="odin-relative odin-flex odin-justify-center odin-h-full">
            <Avatar
              size="md"
              src={imageUrl}
              placeholder={getInitialsForUser(user)}
              objectFit="cover"
            />
            {isOnSite && <ClockIcon className={classes.clockIcon} />}
          </div>
          <div className={classes.workerName}>
            {firstName} {lastName}
          </div>
        </div>
        <div className={classes.workerInfoContainer}>
          {phoneNumber && (
            <div className={classes.workerInfoItem}>
              <PhoneIcon
                className={classes.workerInfoItemIcon}
                aria-hidden="true"
              />
              {getFormattedPhoneNumber(phoneNumber)}
            </div>
          )}
          {tempBadge && (
            <div className={classes.workerInfoItem}>
              <BarcodeReadIcon
                className={classes.workerInfoItemIcon}
                aria-hidden="true"
              />
              {`${tempBadge.name} (#${tempBadge.cardNumber})`}
            </div>
          )}
          {expiredAt && (
            <div className={classes.workerInfoItem}>
              <CalendarXmarkIcon
                className={classes.workerInfoItemIcon}
                aria-hidden="true"
              />
              {getPrettyFormattedDateTime(expiredAt)}
            </div>
          )}
        </div>
      </div>
      <div className={classes.participantTypeContainer}>
        {onRemoveIconClick && (
          <span
            onClick={(ev): void => {
              onRemoveIconClick();
              ev.stopPropagation();
            }}
          >
            <TimesIcon className={classes.removeIcon} aria-hidden="true" />
          </span>
        )}
      </div>
    </div>
  );
}
